import {Component, EventEmitter, inject, OnInit, Output, signal,} from '@angular/core';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators,} from '@angular/forms';
import {forkJoin} from 'rxjs';
import {TimezoneControllerService, TimezoneDto, UserControllerService, UserDto,} from '@api/utisha-backend';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {UserService} from '../../../services/user/user.service';
import {MessageService} from '../../../services/message/message.service';
import {
  UtAvatarUploadComponent,
  UtButtonComponent,
  UtCountryPickerComponent,
  UtDateInputComponent,
  UtFormSectionComponent,
  UtInputComponent,
  UtSelectComponent,
  type UtSelectOption,
} from '../../../ui';
import {languagesAndFlags} from '../../../util/flags';
import {formatLocalDate, parseLocalDate} from '../../../util/local-date';
import UserTypeEnum = UserDto.UserTypeEnum;
import LanguageEnum = UserDto.LanguageEnum;

/**
 * Profile edit form — redesigned around the Utisha UI Kit.
 *
 * The form is split into four `ut-form-section` cards:
 *   1. Personal — avatar, name, birth date
 *   2. Contact  — email (read-only), phone
 *   3. Address  — street, zip, city, country
 *   4. Preferences — language, content-language filter, time zone
 *
 * A sticky action bar at the bottom holds Save + Cancel. Save is disabled
 * until the form is both dirty and valid so unchanged forms can't be
 * accidentally resubmitted.
 */
@Component({
  selector: 'app-profile-form',
  standalone: true,
  templateUrl: './profile-form.component.html',
  styleUrls: ['./profile-form.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    UtFormSectionComponent,
    UtInputComponent,
    UtSelectComponent,
    UtDateInputComponent,
    UtAvatarUploadComponent,
    UtButtonComponent,
    UtCountryPickerComponent,
  ],
})
export class ProfileFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly userService = inject(UserService);
  private readonly userController = inject(UserControllerService);
  private readonly timezoneService = inject(TimezoneControllerService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  @Output() saved = new EventEmitter<UserDto>();
  @Output() cancelled = new EventEmitter<void>();

  protected readonly loading = signal(true);
  protected readonly saving = signal(false);

  protected readonly user = signal<UserDto | undefined>(undefined);

  protected readonly form = this.fb.group({
    personal: this.fb.group({
      avatarUuid: [null as string | null],
      givenName: ['', { validators: [Validators.required], updateOn: 'blur' }],
      middleName: [''],
      lastName: ['', { validators: [Validators.required], updateOn: 'blur' }],
      birthDate: [null as Date | null],
    }),
    contact: this.fb.group({
      email: [
        '',
        { validators: [Validators.required, Validators.email], updateOn: 'blur' },
      ],
      phone: [''],
    }),
    address: this.fb.group({
      street: ['', { validators: [Validators.required], updateOn: 'blur' }],
      houseNumber: ['', { validators: [Validators.required], updateOn: 'blur' }],
      houseNumberExtension: [''],
      zipcode: ['', { validators: [Validators.required], updateOn: 'blur' }],
      city: ['', { validators: [Validators.required], updateOn: 'blur' }],
      country: ['', { validators: [Validators.required] }],
    }),
    preferences: this.fb.group({
      language: [LanguageEnum.En as string, { validators: [Validators.required] }],
      filterLanguages: [[] as string[]],
      timezone: ['', { validators: [Validators.required] }],
    }),
  });

  protected readonly languageOptions: UtSelectOption<string>[] = Object.keys(
    LanguageEnum,
  ).map((k) => ({
    value: (LanguageEnum as any)[k],
    label: this.translate.instant(`language.${(LanguageEnum as any)[k]}`),
  }));

  protected readonly filterLanguageOptions: UtSelectOption<string>[] =
    languagesAndFlags.map((l) => ({
      value: l.language,
      label: this.translate.instant(`language.${l.language}`),
    }));

  protected timezoneOptions: UtSelectOption<string>[] = [];

  ngOnInit(): void {
    forkJoin([
      this.timezoneService.getTimezones(),
      this.userController.getMe(),
    ]).subscribe({
      next: ([timezones, me]) => {
        this.timezoneOptions = timezones.map(this.timezoneToOption);
        this.user.set(me);
        this.loadUser(me);
        this.detectTimezoneIfBlank();
        this.loading.set(false);
      },
      error: (err) => {
        this.messageService.apiError(err);
        this.loading.set(false);
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Template helpers
  // ---------------------------------------------------------------------------
  protected displayName(): string {
    const p = this.form.controls.personal.getRawValue();
    return [p.givenName, p.middleName, p.lastName]
      .filter((x) => !!x && x.trim().length > 0)
      .join(' ');
  }

  protected save(): void {
    if (this.form.invalid || this.saving()) return;
    this.saving.set(true);
    const user = this.formToUserDto();
    const currentUser = this.user();
    const req =
      currentUser?.userType === UserTypeEnum.Teacher
        ? this.userController.updateTeacher(user)
        : this.userController.updateStudent(user);
    req.subscribe({
      next: () => {
        this.messageService.success('profile updated');
        this.userService.refresh().then((u) => {
          this.saving.set(false);
          this.saved.emit(u as UserDto);
        });
      },
      error: (err) => {
        this.messageService.apiError(err);
        this.saving.set(false);
      },
    });
  }

  protected cancel(): void {
    this.cancelled.emit();
  }

  protected fieldError(control: { invalid: boolean; touched: boolean; errors: any }): string {
    if (!control.touched || !control.invalid) return '';
    const e = control.errors || {};
    if (e.required) return this.translate.instant('validation.required');
    if (e.email) return this.translate.instant('validation.email');
    return this.translate.instant('validation.invalid');
  }

  // ---------------------------------------------------------------------------
  // Form ↔ DTO mapping
  // ---------------------------------------------------------------------------
  private loadUser(user: UserDto): void {
    this.form.controls.personal.patchValue({
      avatarUuid: user.avatarUuid ?? null,
      givenName: user.firstName ?? '',
      middleName: user.infix ?? '',
      lastName: user.lastName ?? '',
      // Parse via `parseLocalDate` (Kind C helper). Native
      // `new Date("1990-05-15")` anchors at UTC midnight and reads back
      // as the previous day for anyone west of UTC.
      birthDate: parseLocalDate(user.birthDate),
    });
    this.form.controls.contact.patchValue({
      email: user.email ?? '',
      phone: user.phoneNumber ?? '',
    });
    if (user.address) {
      this.form.controls.address.patchValue({
        street: user.address.street ?? '',
        houseNumber: user.address.houseNumber ? String(user.address.houseNumber) : '',
        houseNumberExtension: user.address.houseNumberExtension ?? '',
        zipcode: user.address.zipCode ?? '',
        city: user.address.city ?? '',
        country: user.address.country?.isoCode ?? '',
      });
    }
    this.form.controls.preferences.patchValue({
      language: user.language ?? LanguageEnum.En,
      filterLanguages: this.parseFilterLanguages(user.filterLanguages),
      timezone: user.timezone ?? '',
    });
    this.form.markAsPristine();
  }

  private formToUserDto(): UserDto {
    const p = this.form.controls.personal.getRawValue();
    const c = this.form.controls.contact.getRawValue();
    const a = this.form.controls.address.getRawValue();
    const pr = this.form.controls.preferences.getRawValue();
    const currentUser = this.user() || ({} as UserDto);
    return {
      ...currentUser,
      firstName: p.givenName,
      infix: p.middleName,
      lastName: p.lastName,
      // Format via `formatLocalDate` — reads the Date's local numerals,
      // not its UTC projection, so the round-trip is day-stable in any
      // timezone. `toISOString().slice(0, 10)` would shift by a day east
      // of UTC and was a latent bug for traveling users.
      birthDate: formatLocalDate(p.birthDate) ?? undefined,
      avatarUuid: p.avatarUuid ?? undefined,
      email: c.email,
      phoneNumber: c.phone,
      address: {
        street: a.street,
        houseNumber: a.houseNumber ? parseInt(a.houseNumber, 10) : undefined,
        houseNumberExtension: a.houseNumberExtension,
        zipCode: a.zipcode,
        city: a.city,
        country: { isoCode: a.country },
      },
      language: pr.language as LanguageEnum,
      filterLanguages: (pr.filterLanguages ?? []) as UserDto['filterLanguages'],
      timezone: pr.timezone,
    } as UserDto;
  }

  private parseFilterLanguages(value: unknown): string[] {
    if (!value) return [];
    if (Array.isArray(value)) return value as string[];
    // Legacy rows may still be a comma-separated string — fall back gracefully.
    return String(value).split(',').filter((v) => v.length > 0);
  }

  private detectTimezoneIfBlank(): void {
    const ctrl = this.form.controls.preferences.controls.timezone;
    if (ctrl.value) return;
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone.toLowerCase();
    const match = this.timezoneOptions.find(
      (o) => o.value.toLowerCase() === detected,
    );
    if (match) ctrl.setValue(match.value);
  }

  private timezoneToOption = (t: TimezoneDto): UtSelectOption<string> => ({
    value: t.zoneId!,
    label: `${t.zoneId} (${t.shortDescription})`,
  });
}
